import { ReloadOutlined } from '@ant-design/icons'
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { Button, Flex, Space, Typography } from 'antd'
import { formatDistanceToNow } from 'date-fns'
import type { ReactNode } from 'react'

/** Page title with the age of the oldest loaded query and a refresh of every query under `queryKey`. */
export function RefreshablePageHeader({
  title,
  queryKey,
  queries,
  children,
}: {
  title: string
  queryKey: string
  queries: Pick<
    UseQueryResult,
    'dataUpdatedAt' | 'isLoading' | 'isRefetching'
  >[]
  children?: ReactNode
}) {
  const queryClient = useQueryClient()
  const timestamps = queries.map((q) => q.dataUpdatedAt).filter((t) => t > 0)
  const lastUpdated =
    timestamps.length > 0 ? new Date(Math.min(...timestamps)) : null

  return (
    <Flex
      justify="space-between"
      align="center"
      wrap
      gap={16}
      style={{ marginBottom: 24 }}
    >
      <h2 style={{ margin: 0 }}>{title}</h2>
      <Space wrap>
        {children}
        {lastUpdated && (
          <Typography.Text type="secondary">
            Updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}
          </Typography.Text>
        )}
        <Button
          icon={<ReloadOutlined spin={queries.some((q) => q.isRefetching)} />}
          onClick={() => {
            void queryClient.invalidateQueries({ queryKey: [queryKey] })
          }}
          loading={queries.some((q) => q.isLoading)}
        >
          Refresh
        </Button>
      </Space>
    </Flex>
  )
}
